import StepType from "../flow/enums/stepType.enum"
import { Flow } from "../flow/flow.component"
import Gender from "../../enums/gender.enum"
import { useState, useContext, useEffect, useRef } from "react"
import { UserContext } from "../../contexts/user.context"
import ProductTypeCode from "../../enums/productTypeCode.enum"
import { SleepApneaTestConfirm } from "./sleepApneaTestConfirm.component"
import ProductHelper from "../../helpers/product.helper"
import { listProductTypes } from '../../services/productType.service'
import { completeFlow } from "../../services/flow.service"
import { useNavigate } from "react-router-dom"
import { updateProduct } from "../../services/product.service.js"

export const Step = {
  PAY: 'pay',
  DELIVERY: 'delivery',
  BASIC_INFO: 'basic-info',
  CONTACT: 'contact',
  PASSWORD: 'password',
  CONFIRM: 'confirm',
}

export const SleepApneaTestFlow = () => {
  const navigate = useNavigate()
  const { instalabMembership, currentUser } = useContext(UserContext)
  const [flow, setFlow] = useState()
  const [skeleton, setSkeleton] = useState()
  const [membershipTypes, setMembershipTypes] = useState()
  const [productTypes, setProductTypes] = useState()
  const [hasPass, setHasPass] = useState()
  const [initialUser, setInitialUser] = useState()
  const hasCompletedFlowRef = useRef(false)

  useEffect(() => {
    fetchSkeleton()
  }, [productTypes, instalabMembership, flow, hasPass, initialUser])

  useEffect(() => {
    fetchProductTypes()
  }, [])


  const fetchProductTypes = async () => {
    const response = await listProductTypes({
      filter: {
        code: {
          $in: [
            ProductTypeCode.WATCHPAT_ONE,
          ]
        }
      }
    })
    setProductTypes(response)
  }

  const getProductType = (code) => {
    return productTypes?.find(productType => productType.code === code)
  }

  const fetchSkeleton = () => {
    if (hasPass === undefined || !initialUser || !productTypes) return

    const totalCost = ProductHelper.getTotalCostV2({
      productTypes: [getProductType(ProductTypeCode.WATCHPAT_ONE)],
      currentUser,
      instalabMembership,
    })

    setSkeleton({

      [Step.PAY]: {
        type: StepType.PAY,
        nextStep: Step.DELIVERY,
        addProduct: true,
        productType: ProductTypeCode.WATCHPAT_ONE,
        title:<>Pay <span className="true-price">${totalCost}</span> for the <span className="product-name">Sleep Apnea Test</span>.</>,
        description: `This includes the WatchPat One device and results and report on your sleep apnea status and sleep quality metrics.`,
      },

     
      [Step.DELIVERY]: {
        type: StepType.LOCATION,
        buildUser: true,
        nextStep: Step.BASIC_INFO,
        title: `What's your delivery address?`,
        description: `We’ll send your sleep apnea test kit to this location.`,
        field: {
          name: 'shippingLocation'
        },
        onSuccess: async (patient) => {
          try {
            for (const product of flow?.products) {
              await updateProduct(product._id, { 
                fields: {shippingLocation: patient.shippingLocation} 
              });
            }
          } 
          catch (error) {
            console.log(error)
          }

          return true
        }
      },

      [Step.BASIC_INFO]: {
        type: StepType.MULTIPLE_FIELDS,
        nextStep: Step.CONTACT,
        buildUser: true,
        title: `Let's get to know you.`,
        description: `We just need a few details to prepare your order.`,
        fields: [
          {
            type: "group",
            label: "Full Name",
            required: true,
            fields: [
              {
                name: 'firstName',
                placeholder: 'First Name',
                label: 'First Name',
                required: true,
                type: 'text',
              },  
              {
                name: 'lastName',
                placeholder: 'Last Name',
                label: 'Last Name',
                required: true,
                type: 'text',
              },
            ]
          },
          {
            name: 'dob',
            label: 'Date of Birth',
            required: true,
            type: 'date',
            placeholder: 'MM/DD/YYYY',
          },
          {
            name: 'gender',
            label: 'Biological Sex',
            required: true,
            type: 'select',
            multiple:false,
            options: [
              {
                label: 'Male',
                value: Gender.MALE,
              }, {
                label: 'Female',
                value: Gender.FEMALE,
              }
            ],
          },
        ],
        skipIf: () => {
          return hasPass && (initialUser?.gender && initialUser?.dob && initialUser?.firstName && initialUser?.lastName)
        }
      },

      [Step.CONTACT]: {
        type: StepType.MULTIPLE_FIELDS,
        nextStep: Step.PASSWORD,
        buildUser: true,
        title: `How can we reach you?`,
        description: "We’ll send your order confirmation, tracking number and results here.",
        fields: [
          {
          name: 'email',
          label: 'Email Address',
          required: true,
          type: 'email',
          unique: true,
        }, {
          name: 'phone',
          label: 'Phone Number',
          required: true,
          type: 'phone',
        }],
        skipIf: () => {
          return hasPass && (initialUser?.email && initialUser?.phone)
        }
      },

                
      [Step.PASSWORD]: {
        type: StepType.SINGLE_INPUT,
        nextStep: Step.CONFIRM,
        buildUser: true,
        title: `Create your password.`,
        description: `This lets you securely access your results, manage orders, and track your health over time.`,
        field: {
          name: 'password',
          placeholder: 'Enter your password',
          required: true,
          password: true,
        },
        skipIf: () => {
          return hasPass
        },
      },

      [Step.CONFIRM]: {
        type: StepType.STATIC,
        showFooter: false,
        title: `All set!`,
        description: `We'll send the sleep apnea test kit within 1 business day. Tracking number will be available in your account if you'd like to track its progress.`,
        enterStyle: { display: 'none' },
        buttonStyle: { display: 'none' },
        content: <SleepApneaTestConfirm/>,
        onLoad: async () => {
          if (flow?._id && !hasCompletedFlowRef.current) {
            hasCompletedFlowRef.current = true; // Mark flow as completed
            await completeFlow(flow._id);
          }
        }
      },
    })
  }

  return (
    <Flow 
      skeleton={skeleton} 
      flow={flow} 
      setFlow={setFlow}
      initialUser={initialUser}
      setInitialUser={setInitialUser}
      setHasPass={setHasPass}
      productTypeCode={ProductTypeCode.WATCHPAT_ONE}
    />
  )
}